import React, { useState, useEffect, useContext } from "react";
import PageWrapper from "../../components/PageWrapper";
import axios from "axios";
import CustomInput from "../../components/CustomInput";
import CustomButton from "../../components/CustomButton";
import EmployeeAttendanceTable from "./EmployeesAttendanceTable";
import Loader from "../../components/Loader";
import { UserAuth } from "../../utils/AuthContext";
import { useTranslation } from "react-i18next";
import useLanguage from "../../utils/useLanguage";
import BackToDashboard from "../../components/BackToDashboard";

import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import InformationBox from "../../components/InformationBox";
import useDebounce from "../../utils/useDebounce";
import ErrorBox from "../../components/ErrorBox";
import saveIcon from "../../assets/save_icon.png";
import closeIcon from "../../assets/close_icon.png";
import AreYouSureModal from "../../components/AreYouSureModal";
import axiosClient from "../../services/axiosClient";

const CalculateAttendance = () => {
  // HOOKS
  const { t } = useTranslation("general");
  const { userData } = useContext(UserAuth);
  const { currentDirection } = useLanguage();

  // State management - grouped together
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState("");
  const debounceSearch = useDebounce(searchTerm, 500);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });
  const [paginationType, setPaginationType] = useState("next");
  const [showAreYouSureModal, setShowAreYouSureModal] = useState(false);

  // Track changed employees
  const [changedEmployees, setChangedEmployees] = useState([]);
  const [savingAll, setSavingAll] = useState(false);

  const isOrgUser = userData?.userType === "orgUser";

  const titles = [
    { value: "name", keepOnMobile: true },
    { value: "status", keepOnMobile: true },
    { value: "checkIn", keepOnMobile: true },
    { value: "checkOut", keepOnMobile: true },
    { value: "hours", keepOnMobile: true },
    ...(isOrgUser
      ? [
          { value: "hourlyRate", keepOnMobile: true },
          { value: "dailySalary", keepOnMobile: true },
        ]
      : []),
    { value: "Actions", keepOnMobile: true },
  ];

  // Helper functions
  const formatDate = (date) => date.toISOString().split("T")[0];

  // Helper function to format time for input fields
  const formatTimeForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toTimeString().substring(0, 5);
  };

  // Add employee to changed list
  const markEmployeeAsChanged = (employeeId) => {
    setChangedEmployees((prev) => {
      if (!prev.includes(employeeId)) {
        return [...prev, employeeId];
      }
      return prev;
    });
  };

  // Main API function to get users and their attendance
  const getUsersWithAttendance = async () => {
    const token = localStorage.getItem("token");
    const organizationId = localStorage.getItem("organizationId");
    const dateKey = formatDate(currentDate);
    const isUser = userData?.userType === "orgUser";

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/api/attendance/getUsersAttendanceInfo`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            page: pagination.currentPage,
            limit: 10,
            search: searchTerm,
            organizationId: organizationId,
            date: dateKey,
          },
        }
      );

      if (response.data && response.data.users) {
        const transformedUsers = response.data.users.map((user) => {
          const attendanceRecord =
            user.attendance && user.attendance.length > 0
              ? user.attendance[0]
              : null;

          let status, checkInTime, checkOutTime, hourlyRate;

          if (attendanceRecord) {
            status = {
              value: attendanceRecord.status,
              title: t(attendanceRecord.status.toLowerCase()),
            };
            checkInTime = formatTimeForInput(attendanceRecord.checkIn);
            checkOutTime = formatTimeForInput(attendanceRecord.checkOut);
            hourlyRate = attendanceRecord.hourlyRate;
          } else {
            status = { value: "PRESENT", title: t("present") };
            checkInTime = "";
            checkOutTime = "";
            hourlyRate = user.hourlySalary || 0;
          }

          const getHourlyRate = () => {
            if (typeof user.hourlySalary === "number") {
              return user.hourlySalary;
            }

            const existingEmployee = employees.find(
              (emp) => emp.id === user.userId
            );

            return existingEmployee?.hourlyRate;
          };

          return {
            id: user.userId,
            name: user.username,
            hourlyRate: isUser ? null : getHourlyRate(),
            logs: {
              [dateKey]: [{ start: checkInTime, end: checkOutTime }],
            },
            status: status,
            email: user.email,
            organization: user.organization?.organizationName,
            attendanceRecord: attendanceRecord,
          };
        });

        setEmployees(transformedUsers);
        setPagination((prev) => ({
          ...prev,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalCount,
        }));

        // Clear changed employees after fetching new data
        setChangedEmployees([]);
      } else {
        setError("Failed to get users data");
      }
    } catch (error) {
      setError("FailedToGetAttendanceData");
      console.error("Failed to get the users", error);
    } finally {
      setLoading(false);
    }
  };

  const saveAttendance = async (employee, dateKey) => {
    const token = localStorage.getItem("token");
    const organizationId = localStorage.getItem("organizationId");

    let checkIn, checkOut, totalHours, salary;

    switch (employee.status.value) {
      case "ABSENT":
        checkIn = 0;
        checkOut = 0;
        totalHours = 0;
        salary = 0;
        break;

      case "OFFDAY":
        checkIn = new Date(`${dateKey}T00:00:00`);
        checkOut = new Date(`${dateKey}T00:00:00`);
        totalHours = 0;
        salary = 0;
        break;

      case "VACATION":
        checkIn = new Date(`${dateKey}T09:00:00`);
        checkOut = new Date(`${dateKey}T17:00:00`);
        totalHours = 8;
        salary = 8 * employee.hourlyRate;
        break;

      default:
        const dailyLogs = employee.logs[dateKey] || [];
        const log = dailyLogs[dailyLogs.length - 1];

        if (!log?.start || !log?.end) {
          alert("Please enter both check-in and check-out times");
          return;
        }

        checkIn = new Date(`${dateKey}T${log.start}:00`);
        checkOut = new Date(`${dateKey}T${log.end}:00`);

        if (checkOut < checkIn) {
          checkOut.setDate(checkOut.getDate() + 1);
        }

        totalHours = (checkOut - checkIn) / (1000 * 60 * 60);
        salary = totalHours * employee.hourlyRate;
        break;
    }

    const attendanceData = {
      userId: employee.id,
      status: employee.status.value,
      hourlyRate: employee.hourlyRate,
      createdAt: checkIn,
      checkIn: checkIn,
      checkOut: checkOut,
      totalHours,
      salary: salary,
      organizationId: parseInt(organizationId),
    };

    try {
      const method = "post";
      const url = `${process.env.REACT_APP_URL}/api/attendance/createAttendance`;

      await axios[method](url, attendanceData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "organization-id": organizationId,
        },
      });

      alert("Attendance saved successfully");

      return true;
    } catch (error) {
      setError(error?.response?.data?.message);
      console.error("Failed to save attendance:", error);
    }
  };

  // Save all changed employees
  const handleSaveAll = async () => {
    if (changedEmployees.length === 0) return;
    const organizationId = localStorage.getItem("organizationId");
    const dateKey = formatDate(currentDate);
    const employeesData = changedEmployees.map((id) => {
      const foundEmployee = employees.find((emp) => emp.id === id);
      if (foundEmployee) {
        let checkIn, checkOut, totalHours, salary;

        switch (foundEmployee.status.value) {
          case "ABSENT":
            checkIn = 0;
            checkOut = 0;
            totalHours = 0;
            salary = 0;
            break;

          case "OFFDAY":
            checkIn = new Date(`${dateKey}T00:00:00`);
            checkOut = new Date(`${dateKey}T00:00:00`);
            totalHours = 0;
            salary = 0;
            break;

          case "VACATION":
            checkIn = new Date(`${dateKey}T09:00:00`);
            checkOut = new Date(`${dateKey}T17:00:00`);
            totalHours = 8;
            salary = 8 * foundEmployee.hourlyRate;
            break;

          default:
            const dailyLogs = foundEmployee.logs[dateKey] || [];
            const log = dailyLogs[dailyLogs.length - 1];

            if (!log?.start || !log?.end) {
              alert("Please enter both check-in and check-out times");
              return;
            }

            checkIn = new Date(`${dateKey}T${log.start}:00`);
            checkOut = new Date(`${dateKey}T${log.end}:00`);

            if (checkOut < checkIn) {
              checkOut.setDate(checkOut.getDate() + 1);
            }

            totalHours = (checkOut - checkIn) / (1000 * 60 * 60);
            salary = totalHours * foundEmployee.hourlyRate;
            break;
        }

        const attendanceData = {
          userId: foundEmployee.id,
          status: foundEmployee.status.value,
          hourlyRate: foundEmployee.hourlyRate,
          createdAt: checkIn,
          checkIn: checkIn,
          checkOut: checkOut,
          totalHours,
          salary: salary,
          organizationId: parseInt(organizationId),
        };
        return attendanceData;
      }
    });

    try {
      setSavingAll(true);

      await axiosClient.post(`/api/attendance/createMultiAttendance`, {
        records: employeesData,
      });

      alert("Attendance saved successfully");
      setChangedEmployees([]);
      getUsersWithAttendance();
    } catch (error) {
      console.error("Error during bulk save:", error);
      alert("An error occurred while saving attendance records");
    } finally {
      setSavingAll(false);
    }
  };

  // Event handlers - grouped together
  const handleLogChange = (id, field, value) => {
    markEmployeeAsChanged(id);
    setEmployees((prev) =>
      prev.map((emp) => {
        if (emp.id !== id) return emp;

        const dateKey = formatDate(currentDate);
        const logs = { ...emp.logs };

        const existingLog = logs[dateKey]?.[0] || { start: "", end: "" };
        let updatedLog;

        if (field === "start") {
          updatedLog = {
            ...existingLog,
            [field]: value,
            end: existingLog.end || "17:00",
          };
        } else {
          updatedLog = {
            ...existingLog,
            [field]: value,
          };
        }

        logs[dateKey] = [updatedLog];

        return { ...emp, logs };
      })
    );
  };

  const handleHourlyRateChange = (id, value) => {
    markEmployeeAsChanged(id);
    setEmployees((prev) =>
      prev.map((emp) => {
        if (emp.id === id) {
          return { ...emp, hourlyRate: value };
        }
        return emp;
      })
    );
  };

  const handleStatusChange = (employeeId, status) => {
    markEmployeeAsChanged(employeeId);
    setEmployees((prev) =>
      prev.map((emp) => {
        if (emp.id === employeeId) {
          return {
            ...emp,
            status,
          };
        }
        return emp;
      })
    );
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  // Navigation handlers
  const prevDay = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
  };

  const nextDay = () => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() + 1);
      return newDate;
    });
  };

  const calculateDailySalary = (logs, hourlyRate, dateKey) => {
    const dailyLogs = logs[dateKey] || [];

    let totalHours = dailyLogs.reduce((sum, log) => {
      if (log.start && log.end) {
        const startTime = new Date(`1970-01-01T${log.start}:00`);
        const endTime = new Date(`1970-01-01T${log.end}:00`);

        let hours = (endTime - startTime) / (1000 * 60 * 60);
        if (hours < 0) {
          hours = 24 + hours;
        }

        return sum + (hours > 0 ? hours : 0);
      }
      return sum;
    }, 0);

    return totalHours > 0 ? (totalHours * hourlyRate).toFixed(2) : "0.00";
  };

  // USEEFFECTS
  useEffect(() => {
    getUsersWithAttendance();
  }, [pagination.currentPage, debounceSearch, currentDate]);
  // http://localhost:3005/api/attendance/updateMultiEmployeeAttendance
  return (
    <PageWrapper>
      <div>
        <div>
          <BackToDashboard
            title={t("back")}
            variant="blank"
            icon={currentDirection === "ltr" ? previousIcon : nextIcon}
            iconSize={14}
            to={
              userData?.userType === "user"
                ? "/invoiceManagement"
                : "/attendance-dashboard"
            }
          />
        </div>

        <div className="o-row o-row--gap-size_100 t-bold_bold u-margin--top_600">
          <span>
            {userData?.userType === "organization"
              ? `${t("organization")}:`
              : `${t("user")}:`}
          </span>
          <span>{`${localStorage.getItem("username")}`}</span>
        </div>

        <h1>{t("attendance")}</h1>

        {/* Save All Button - Shows when there are changes */}
        {changedEmployees.length > 0 && (
          <div className="o-row o-row--gap-size_200 u-margin--bottom_400 o-row--horizontal-align_end">
            <CustomButton
              title={t("cancel")}
              variant="blank"
              onClick={() => {
                setChangedEmployees([]);
                getUsersWithAttendance();
              }}
              icon={closeIcon}
              iconSize={15}
              disabled={savingAll}
            />
            <CustomButton
              title={`${t("saveAll")} (${changedEmployees.length})`}
              variant="primary"
              onClick={handleSaveAll}
              icon={saveIcon}
              iconSize={18}
              disabled={savingAll}
            />
          </div>
        )}

        {/* Search and date navigation */}
        <div className="o-row o-row--horizontal-align_space-between u-margin--bottom_400 o-column-row-switcher o-row--gap-size_400">
          <CustomInput
            type="text"
            placeholder={t("search")}
            value={searchTerm}
            onChange={handleSearch}
            resetSearch={() => setSearchTerm("")}
            thick
          />
          <div className="o-row o-row--horizontal-align_space-between o-row--vertical-align_center o-row--gap-size_200">
            <CustomButton
              title={t("previousDay")}
              variant="blank"
              onClick={() => {
                if (changedEmployees.length > 0) {
                  setPaginationType("previous");
                  setShowAreYouSureModal(true);
                  return;
                }
                prevDay();
              }}
              icon={currentDirection === "ltr" ? previousIcon : nextIcon}
              iconSize={15}
              mobileEffect
            />

            <CustomInput
              value={formatDate(currentDate)}
              thick
              type="date"
              onChange={(e) => {
                const selectedDate = new Date(e.target.value);
                setCurrentDate(selectedDate);
              }}
            />

            <CustomButton
              title={t("nextDay")}
              variant="blank"
              onClick={() => {
                if (changedEmployees.length > 0) {
                  setPaginationType("next");
                  setShowAreYouSureModal(true);
                  return;
                }
                nextDay();
              }}
              icon={currentDirection === "ltr" ? nextIcon : previousIcon}
              iconSize={15}
              reverseIcon
              mobileEffect
            />
          </div>
        </div>

        {loading && <Loader />}
        {error && (
          <div className="u-margin--top_700">
            <ErrorBox>{t(error)}</ErrorBox>
          </div>
        )}

        {!error && !loading && employees.length === 0 ? (
          <InformationBox>{t("noEmployeesFound")}</InformationBox>
        ) : (
          !loading &&
          !error && (
            <EmployeeAttendanceTable
              titles={titles}
              employees={employees}
              setEmployees={setEmployees}
              currentDate={currentDate}
              formatDate={formatDate}
              onSaveAttendance={saveAttendance}
              onHourlyRateChange={handleHourlyRateChange}
              onLogChange={handleLogChange}
              handleStatusChange={handleStatusChange}
              calculateDailySalary={calculateDailySalary}
              pagination={pagination}
              setPagination={setPagination}
              changedEmployees={changedEmployees}
            />
          )
        )}
      </div>
      {showAreYouSureModal && (
        <AreYouSureModal
          show={showAreYouSureModal}
          onClose={() => setShowAreYouSureModal(false)}
          customMessage={t("theCurrentUpdatesWillBeLost")}
          onCancel={() => setShowAreYouSureModal(false)}
          confirmIconType="whiteCheck"
          onConfirm={() => {
            if (paginationType === "next") {
              setChangedEmployees([]);
              nextDay();
            } else {
              setChangedEmployees([]);
              prevDay();
            }
            setShowAreYouSureModal(false);
          }}
        />
      )}
    </PageWrapper>
  );
};

export default CalculateAttendance;
